import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress, // Issue #306: Added for loading indicators
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Grid } from '../components/common/TypedGrid';
import {
  Visibility,
  Check,
  Close,
  Search,
  Download,
  Person,
  VerifiedUser,
  Block,
  Edit,
  AccountBalanceWallet,
  Assignment,
  Receipt,
  CurrencyRupee,
  LocationOn,
  Badge as BadgeIcon,
  AccountBalance,
  CheckCircle,
  Cancel,
  Phone,
  Lock,
  Add,
} from '@mui/icons-material';
import { PageHeader, ResponsiveTable, StatCard, StatusChip } from '../components/common';
import { useAdmin } from '../contexts/AdminContext';
import { exportToCSV as exportToCSVShared } from '../utils/csvExport';
import adminService, {
  NinjaProfile,
  RejectedFields,
  TextFieldRejectionReason,
  ImageFieldRejectionReason,
  City,
} from '../services/adminService';
import { db, storage } from '../config/firebase';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

const serviceSegmentLabels: { [key: string]: string } = {
  delivery: 'Delivery Services',
  home_services: 'Home Services',
  personal_care: 'Personal Care',
  professional: 'Professional Services',
  transportation: 'Transportation',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`ninja-tabpanel-${index}`}
    aria-labelledby={`ninja-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const NinjaManagement: React.FC = () => {
  const { refreshDashboardStats, isSuperAdmin, isActingAsCityAdmin, effectiveCityId } = useAdmin();
  const [ninjas, setNinjas] = useState<NinjaProfile[]>([]);
  const [filteredNinjas, setFilteredNinjas] = useState<NinjaProfile[]>([]);

  // ── Add Ninja (requirement #1) ────────────────────────────────────────────
  const [addNinjaOpen, setAddNinjaOpen] = useState(false);
  const [addNinjaSaving, setAddNinjaSaving] = useState(false);
  const [addNinjaCities, setAddNinjaCities] = useState<City[]>([]);
  const [newNinjaName, setNewNinjaName] = useState('');
  const [newNinjaPhone, setNewNinjaPhone] = useState('');
  const [newNinjaEmail, setNewNinjaEmail] = useState('');
  const [newNinjaCityId, setNewNinjaCityId] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedNinja, setSelectedNinja] = useState<NinjaProfile | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSegment, setFilterSegment] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [ninjaWallet, setNinjaWallet] = useState<any>(null);
  const [ninjaTransactions, setNinjaTransactions] = useState<any[]>([]);
  const [ninjaTasks, setNinjaTasks] = useState<any[]>([]);
  // Tracks a preview image that failed to load, so the fallback can be
  // rendered by React rather than injected as raw HTML — see below.
  const [documentPreviewFailed, setDocumentPreviewFailed] = useState(false);
  const [documentModal, setDocumentModal] = useState<{ open: boolean; type: string; url: string }>({
    open: false,
    type: '',
    url: ''
  });
  const [serviceSegmentsMap, setServiceSegmentsMap] = useState<{ [key: string]: string }>({});
  const [serviceCategoriesMap, setServiceCategoriesMap] = useState<{ [key: string]: string }>({});

  // Reject Modal State - Issue #165
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedFieldsForRejection, setSelectedFieldsForRejection] = useState<Set<string>>(new Set());
  const [rejectionReasons, setRejectionReasons] = useState<{ [field: string]: string }>({});

  // Issue #306: Loading states for Reject/Approve buttons
  const [approvingNinja, setApprovingNinja] = useState(false);
  const [rejectingNinja, setRejectingNinja] = useState(false);

  // Field definitions for rejection modal
  const rejectableFields = {
    personalDetails: [
      { id: 'fullName', label: 'Name', type: 'text' },
      { id: 'dateOfBirth', label: 'Date of Birth', type: 'text' },
      { id: 'gender', label: 'Gender', type: 'text' },
    ],
    addressDetails: [
      { id: 'permanentAddress', label: 'Permanent Address', type: 'text' },
      { id: 'currentAddress', label: 'Current Address', type: 'text' },
    ],
    documentNumbers: [
      { id: 'aadharNumber', label: 'Aadhaar Number', type: 'text' },
      { id: 'dlNumber', label: 'Driving License Number', type: 'text' },
      { id: 'panNumber', label: 'PAN Number', type: 'text' },
    ],
    documentImages: [
      { id: 'aadharFrontImage', label: 'Aadhaar Front Image', type: 'image' },
      { id: 'aadharBackImage', label: 'Aadhaar Back Image', type: 'image' },
      { id: 'dlImage', label: 'Driving License Image', type: 'image' },
      { id: 'panImage', label: 'PAN Card Image', type: 'image' },
    ],
  };

  const textFieldReasons: TextFieldRejectionReason[] = [
    'Mismatch with document',
    'Incorrect details entered',
    'Incomplete information',
    'Typo / spelling mismatch',
  ];

  const imageFieldReasons: ImageFieldRejectionReason[] = [
    'Image is blurred',
    'Document not clearly visible',
    'Wrong document uploaded',
    'Partial image uploaded',
  ];

  // Helper function to get download URL from Firebase Storage path
  const getDocumentUrl = async (urlOrPath: string): Promise<string> => {
    if (!urlOrPath) return '';

    // If it's already a full HTTP(S) URL, return it
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
      return urlOrPath;
    }

    // If it's a file:// URL, it won't work in browser - return as is to show error message
    if (urlOrPath.startsWith('file://')) {
      return urlOrPath;
    }

    // Otherwise, assume it's a Firebase Storage path and get download URL
    try {
      const storageRef = ref(storage, urlOrPath);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error getting download URL:', error);
      return urlOrPath; // Return original path if conversion fails
    }
  };

  // Export functionality
  const exportToCSV = (data: any[], filename: string) => {
    exportToCSVShared(data, filename, () =>
      setSnackbar({ open: true, message: 'No data to export', severity: 'error' })
    );
    if (data && data.length > 0) {
      setSnackbar({ open: true, message: `${filename} exported successfully!`, severity: 'success' });
    }
  };

  const getAllSegments = () => {
    const segments = new Set<string>();
    ninjas.forEach(ninja => {
      if (ninja.selectedSegments) {
        ninja.selectedSegments.forEach(seg => segments.add(seg));
      }
    });
    return Array.from(segments);
  };

  const handleExportNinjas = async () => {
    // Issue #258: Fetch real-time data from ninja_wallets and tasks for accurate export
    try {
      console.log('💾 Issue #258: Fetching real-time ninja data for export...');

      // Issue #265: Fetch all ninja wallets for earnings
      const walletsRef = collection(db, 'ninja_wallets');
      const walletsSnapshot = await getDocs(walletsRef);
      const ninjaEarningsMap = new Map<string, number>();

      walletsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Issue #265: Check both totalEarnings and totalCredits fields
        const totalEarnings = data.totalEarnings || data.totalCredits || 0;
        // Store by document ID
        ninjaEarningsMap.set(doc.id, totalEarnings);
        // Issue #265: Also store by ninjaId field if different from doc.id
        if (data.ninjaId && data.ninjaId !== doc.id) {
          ninjaEarningsMap.set(data.ninjaId, totalEarnings);
        }
        if (data.userId && data.userId !== doc.id) {
          ninjaEarningsMap.set(data.userId, totalEarnings);
        }
      });

      // Fetch all tasks for job counts and ratings
      const tasksRef = collection(db, 'tasks');
      const tasksSnapshot = await getDocs(tasksRef);
      const ninjaStatsMap = new Map<string, { totalJobs: number; totalRating: number; ratingCount: number }>();

      tasksSnapshot.docs.forEach(doc => {
        const task = doc.data();
        const ninjaId = task.assignedTo || task.ninjaId;

        if (ninjaId && task.status === 'completed') {
          if (!ninjaStatsMap.has(ninjaId)) {
            ninjaStatsMap.set(ninjaId, { totalJobs: 0, totalRating: 0, ratingCount: 0 });
          }

          const stats = ninjaStatsMap.get(ninjaId)!;
          stats.totalJobs++;

          // Add rating if exists
          if (task.rating && task.rating > 0) {
            stats.totalRating += task.rating;
            stats.ratingCount++;
          }
        }
      });

      console.log(`✅ Issue #258: Loaded data for ${ninjaEarningsMap.size} wallets and ${ninjaStatsMap.size} ninjas`);

      const exportData = filteredNinjas.map(ninja => {
        // Convert segment IDs to names
        const segmentNames = (ninja.selectedSegments || []).map(segmentId =>
          serviceSegmentsMap[segmentId] || serviceCategoriesMap[segmentId] || serviceSegmentLabels[segmentId] || segmentId
        ).join(', ');

        // Issue #214: Format phone number to prevent Excel scientific notation
        // Prefix with apostrophe to force Excel to treat as text
        const phoneNumber = ninja.phoneNumber || ninja.phone || '';
        const formattedPhone = phoneNumber ? `'${phoneNumber}` : 'No Phone';

        // Issue #258, #261, #264: Get real-time data with priority fallbacks
        // Priority for jobs: ninja.totalJobs > calculated from tasks > 0
        // Priority for earnings: ninja.totalEarnings > ninja_wallets.totalCredits > 0
        // Priority for rating: ninja.rating > calculated from tasks > 0
        const ninjaId = ninja.id || ninja.userId;
        const stats = ninjaStatsMap.get(ninjaId) || { totalJobs: 0, totalRating: 0, ratingCount: 0 };

        // Issue #264: Use ninja.totalJobs from ninjas collection first, fallback to calculated
        const ninjaTotalJobs = (ninja as any).totalJobs || 0;
        const totalJobs = ninjaTotalJobs > 0 ? ninjaTotalJobs : stats.totalJobs;

        // Issue #261, #264: Use ninja.totalEarnings from ninjas collection first, fallback to wallet
        const earnings = (ninja as any).totalEarnings && (ninja as any).totalEarnings > 0
          ? (ninja as any).totalEarnings
          : (ninjaEarningsMap.get(ninjaId) || 0);

        // Issue #261: Use ninja.rating from ninjas collection first, fallback to calculated
        const ninjaRating = (ninja as any).rating || (ninja as any).avgRating || 0;
        const calculatedRating = stats.ratingCount > 0 ? (stats.totalRating / stats.ratingCount) : 0;
        const averageRating = ninjaRating > 0 ? ninjaRating.toFixed(1) : (calculatedRating > 0 ? calculatedRating.toFixed(1) : '0');

        // Email is not required for ninja registration, only include if exists
        const exportRow: Record<string, any> = {
          'Ninja ID': ninja.id,
          'Full Name': ninja.fullName || 'No Name',
          'Phone Number': formattedPhone,
          'Status': ninja.verificationStatus,
          'Total Jobs': totalJobs,
          'Total Earnings (INR)': earnings,
          'Rating': averageRating,
          'Address': ninja.address ? `${ninja.address.street || ''}, ${ninja.address.city || ''}, ${ninja.address.state || ''} - ${ninja.address.pincode || ''}` : 'Not provided',
          'Service Segments': segmentNames || 'Not specified',
          'Service Radius (km)': ninja.serviceArea?.radius || 0,
          'Bank Account Number': ninja.bankAccountNumber || ninja.bankingDetails?.accountNumber || 'Not provided',
          'IFSC Code': ninja.ifscCode || ninja.bankingDetails?.ifscCode || 'Not provided',
          'Bank Name': ninja.bankingDetails?.bankName || 'Not provided',
          'Joined Date': ninja.createdAt ? new Date(ninja.createdAt).toLocaleDateString() : '',
          'Approved Date': ninja.approvedAt ? new Date(ninja.approvedAt).toLocaleDateString() : ''
        };
        // Only include email in export if it exists
        if (ninja.email) {
          exportRow['Email'] = ninja.email;
        }
        return exportRow;
      });

      exportToCSV(exportData, 'ninjas_export');
    } catch (error) {
      console.error('❌ Issue #258: Error exporting ninjas:', error);
      setSnackbar({ open: true, message: 'Error exporting ninja data', severity: 'error' });
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadServiceData = useCallback(async () => {
    try {
      // Fetch service segments
      const segmentsRef = collection(db, 'service_segments');
      const segmentsQuery = query(segmentsRef, where('isActive', '==', true));
      const segmentsSnapshot = await getDocs(segmentsQuery);
      const segmentsMap: { [key: string]: string } = {};
      segmentsSnapshot.forEach((doc) => {
        const data = doc.data();
        segmentsMap[doc.id] = data.name || doc.id;
      });
      setServiceSegmentsMap(segmentsMap);

      // Fetch service categories
      const categoriesRef = collection(db, 'service_categories');
      const categoriesQuery = query(categoriesRef, where('isActive', '==', true));
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesMap: { [key: string]: string } = {};
      categoriesSnapshot.forEach((doc) => {
        const data = doc.data();
        categoriesMap[doc.id] = data.name || doc.id;
      });
      setServiceCategoriesMap(categoriesMap);
      
      console.log('Loaded service segments:', segmentsMap);
      console.log('Loaded service categories:', categoriesMap);
    } catch (error) {
      console.error('Error loading service data:', error);
    }
  }, []);

  const loadNinjas = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Loading ninjas...');
      // effectiveCityId is a city_admin's own city, or a super_admin's
      // impersonation target — null/undefined (a super_admin's normal view)
      // means every city, unchanged from before city-scoping existed.
      const allNinjas = await adminService.getAllNinjas(effectiveCityId);
      console.log('Loaded ninjas:', allNinjas);
      setNinjas(allNinjas);
    } catch (error) {
      console.error('Error loading ninjas:', error);
      showSnackbar('Error loading ninjas', 'error');
    } finally {
      setLoading(false);
    }
  }, [effectiveCityId]);

  const openAddNinja = useCallback(async () => {
    setNewNinjaName('');
    setNewNinjaPhone('');
    setNewNinjaEmail('');
    // A real city_admin, or a super_admin impersonating one, can only ever
    // create a ninja in that city — fixed, not a choice. Only a super_admin
    // NOT currently viewing a specific city gets a free picker.
    setNewNinjaCityId(isActingAsCityAdmin ? effectiveCityId || '' : '');
    if (isSuperAdmin && !isActingAsCityAdmin) {
      const cities = await adminService.getAllCities();
      setAddNinjaCities(cities.filter((c) => c.isActive));
    }
    setAddNinjaOpen(true);
  }, [isActingAsCityAdmin, effectiveCityId, isSuperAdmin]);

  const handleAddNinja = useCallback(async () => {
    if (!newNinjaName.trim() || !newNinjaPhone.trim()) return;
    if (!isActingAsCityAdmin && !newNinjaCityId) {
      showSnackbar('Pick a city for this ninja', 'error');
      return;
    }
    setAddNinjaSaving(true);
    try {
      await adminService.createNinja({
        fullName: newNinjaName.trim(),
        phoneNumber: newNinjaPhone.trim(),
        email: newNinjaEmail.trim() || undefined,
        cityId: isActingAsCityAdmin ? effectiveCityId || undefined : newNinjaCityId,
      });
      showSnackbar('Ninja added and approved', 'success');
      setAddNinjaOpen(false);
      await loadNinjas();
      refreshDashboardStats();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to add ninja', 'error');
    } finally {
      setAddNinjaSaving(false);
    }
  }, [
    newNinjaName,
    newNinjaPhone,
    newNinjaEmail,
    newNinjaCityId,
    isActingAsCityAdmin,
    effectiveCityId,
    loadNinjas,
    refreshDashboardStats,
  ]);

  const filterNinjas = useCallback(() => {
    let filtered = ninjas;

    // Filter by tab status
    switch (tabValue) {
      case 0: // Issue #276: All ninjas (include deleted)
        // Show all ninjas without filtering by status
        break;
      case 1: // Pending
        filtered = filtered.filter(ninja => ninja.verificationStatus === 'pending');
        break;
      case 2: // Approved
        filtered = filtered.filter(ninja => ninja.verificationStatus === 'approved');
        break;
      case 3: // Rejected
        filtered = filtered.filter(ninja => ninja.verificationStatus === 'rejected');
        break;
      case 4: // Issue #262: Deleted
        filtered = filtered.filter(ninja => ninja.verificationStatus === 'deleted');
        break;
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(ninja =>
        (ninja.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ninja.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ninja.phoneNumber || ninja.phone || '').includes(searchTerm)
      );
    }

    // Filter by status dropdown
    if (filterStatus !== 'all') {
      filtered = filtered.filter(ninja => ninja.verificationStatus === filterStatus);
    }

    // Filter by segment
    if (filterSegment !== 'all') {
      filtered = filtered.filter(ninja => 
        ninja.selectedSegments && ninja.selectedSegments.includes(filterSegment)
      );
    }

    setFilteredNinjas(filtered);
  }, [ninjas, tabValue, searchTerm, filterStatus, filterSegment]);

  useEffect(() => {
    loadNinjas();
    loadServiceData();

    // Set up real-time listener for ninjas collection
    const ninjasQuery = query(collection(db, 'ninjas'));
    const unsubscribe = onSnapshot(ninjasQuery, (snapshot) => {
      console.log('📊 Ninjas collection updated - refreshing list');
      loadNinjas();
    }, (error) => {
      console.error('Error listening to ninjas:', error);
    });

    return () => unsubscribe();
  }, [loadNinjas, loadServiceData]);

  useEffect(() => {
    filterNinjas();
  }, [filterNinjas]);

  const handleApproveNinja = async (ninjaId: string) => {
    if (!window.confirm('Approve this ninja? They will be able to accept jobs immediately.')) {
      return;
    }
    // Issue #306: Set loading state to show loader and prevent multiple clicks
    setApprovingNinja(true);
    try {
      await adminService.approveNinja(ninjaId);
      await loadNinjas();
      await refreshDashboardStats();
      showSnackbar('Ninja approved successfully', 'success');
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error approving ninja:', error);
      showSnackbar('Error approving ninja', 'error');
    } finally {
      // Issue #306: Always reset loading state
      setApprovingNinja(false);
    }
  };

  // Open the reject modal instead of directly rejecting
  const handleOpenRejectModal = () => {
    setSelectedFieldsForRejection(new Set());
    setRejectionReasons({});
    setRejectModalOpen(true);
  };

  // Handle field checkbox toggle
  const handleFieldToggle = (fieldId: string) => {
    const newSelected = new Set(selectedFieldsForRejection);
    if (newSelected.has(fieldId)) {
      newSelected.delete(fieldId);
      // Remove the reason when field is unchecked
      const newReasons = { ...rejectionReasons };
      delete newReasons[fieldId];
      setRejectionReasons(newReasons);
    } else {
      newSelected.add(fieldId);
    }
    setSelectedFieldsForRejection(newSelected);
  };

  // Handle reason selection for a field
  const handleReasonChange = (fieldId: string, reason: string) => {
    setRejectionReasons(prev => ({
      ...prev,
      [fieldId]: reason
    }));
  };

  // Check if submit is valid (at least one field selected, all selected have reasons)
  // Issue #169: Use Array.from() to avoid ES5 Set iteration error
  const isRejectSubmitValid = () => {
    if (selectedFieldsForRejection.size === 0) return false;
    const fieldsArray = Array.from(selectedFieldsForRejection);
    for (let i = 0; i < fieldsArray.length; i++) {
      const fieldId = fieldsArray[i];
      if (!rejectionReasons[fieldId]) return false;
    }
    return true;
  };

  // Submit the rejection with field-level details
  const handleSubmitRejection = async () => {
    if (!selectedNinja || !isRejectSubmitValid()) return;

    // Issue #306: Set loading state to show loader and prevent multiple clicks
    setRejectingNinja(true);
    try {
      // Build the rejectedFields object
      // Issue #169: Use Array.from() to avoid ES5 Set iteration error
      const rejectedFields: RejectedFields = {};
      const fieldsArray = Array.from(selectedFieldsForRejection);
      for (let i = 0; i < fieldsArray.length; i++) {
        const fieldId = fieldsArray[i];
        rejectedFields[fieldId] = rejectionReasons[fieldId] as TextFieldRejectionReason | ImageFieldRejectionReason;
      }

      // Create a summary reason
      const reason = `Rejected fields: ${Array.from(selectedFieldsForRejection).join(', ')}`;

      await adminService.rejectNinja(selectedNinja.id, reason, rejectedFields);
      await loadNinjas();
      await refreshDashboardStats();
      showSnackbar('Ninja rejected with field-level feedback', 'success');
      setRejectModalOpen(false);
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error rejecting ninja:', error);
      showSnackbar('Error rejecting ninja', 'error');
    } finally {
      // Issue #306: Always reset loading state
      setRejectingNinja(false);
    }
  };

  // Legacy direct reject function (kept for backward compatibility)
  const handleRejectNinja = async (ninjaId: string) => {
    try {
      await adminService.rejectNinja(ninjaId);
      await loadNinjas();
      await refreshDashboardStats();
      showSnackbar('Ninja rejected', 'success');
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error rejecting ninja:', error);
      showSnackbar('Error rejecting ninja', 'error');
    }
  };

  const loadNinjaDetails = async (ninja: NinjaProfile) => {
    try {
      // Add mock delivery capabilities if not present (for demo purposes)
      if (!ninja.deliveryCapabilities) {
        // Create different variations based on ninja id for demo
        const variations = [
          {
            fragileItems: true,
            maxDistance: 20,
            maxWeight: 15,
            refrigerated: true,
            vehicleType: "car"
          },
          {
            fragileItems: false,
            maxDistance: 15,
            maxWeight: 10,
            refrigerated: false,
            vehicleType: "bike"
          },
          {
            fragileItems: true,
            maxDistance: 25,
            maxWeight: 25,
            refrigerated: false,
            vehicleType: "van"
          },
          {
            fragileItems: false,
            maxDistance: 12,
            maxWeight: 8,
            refrigerated: false,
            vehicleType: "bicycle"
          }
        ];

        // Use ninja id hash to select variation consistently
        const variationIndex = ninja.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % variations.length;
        ninja.deliveryCapabilities = variations[variationIndex];
      }

      // Load wallet information
      // Try ninja.userId first, then ninja.id as fallback
      let walletDoc = await adminService.getNinjaWallet(ninja.userId);
      if (!walletDoc && ninja.id !== ninja.userId) {
        console.log('Wallet not found with userId, trying ninja.id:', ninja.id);
        walletDoc = await adminService.getNinjaWallet(ninja.id);
      }
      // If still no wallet from ninja_wallets, check embedded wallet in ninja document
      if (!walletDoc && ninja.wallet) {
        console.log('Using embedded wallet from ninja document');
        walletDoc = {
          id: `embedded_${ninja.id}`,
          balance: ninja.wallet?.balance || 0,
          totalCredits: ninja.wallet?.totalCredits || 0,
          totalDebits: ninja.wallet?.totalDebits || 0,
          updatedAt: ninja.wallet?.updatedAt || ninja.updatedAt,
          source: 'embedded_ninja_wallet'
        };
      }
      setNinjaWallet(walletDoc);

      // Load transaction history
      // Try ninja.userId first, then ninja.id as fallback
      let transactions = await adminService.getNinjaTransactions(ninja.userId);
      if ((!transactions || transactions.length === 0) && ninja.id !== ninja.userId) {
        console.log('Transactions not found with userId, trying ninja.id:', ninja.id);
        transactions = await adminService.getNinjaTransactions(ninja.id);
      }
      setNinjaTransactions(transactions);

      // Load task history
      const tasks = await adminService.getNinjaTasks(ninja.id);
      setNinjaTasks(tasks);

      // Update selectedNinja with calculated performance data
      const completedTasks = tasks.filter(t => t.status === 'completed');
      const totalJobs = completedTasks.length;
      const totalEarnings = walletDoc?.totalEarnings || walletDoc?.totalCredits || 0;

      // Update selectedNinja state with fresh calculated data
      setSelectedNinja({
        ...ninja,
        totalJobs: totalJobs,
        totalEarnings: totalEarnings,
        // Preserve existing rating or use from ninja document
        rating: ninja.rating || 0
      });

      console.log(`✅ [loadNinjaDetails]: Updated ninja performance - Jobs: ${totalJobs}, Earnings: ₹${totalEarnings}`);
    } catch (error) {
      console.error('Error loading ninja details:', error);
      setSnackbar({
        open: true,
        message: 'Could not load this partner\'s job and earnings history — the figures shown may be out of date.',
        severity: 'error',
      });
    }
  };

  const handleViewDetails = async (ninja: NinjaProfile) => {
    setSelectedNinja(ninja);
    setDetailsOpen(true);
    setDetailsTabValue(0);
    loadNinjaDetails(ninja);

    // Issue #352: identity and banking details live in a private subcollection,
    // not on the profile document, because the profile is readable by every
    // authenticated user. Fetch them only when an application is actually
    // opened for review, and merge so the fields below render unchanged.
    //
    // Profiles that predate the migration still carry the fields inline; the
    // merge keeps those working because the private record comes back empty.
    try {
      const privateDetails = await adminService.getNinjaPrivateDetails(ninja.id);
      if (Object.keys(privateDetails).length > 0) {
        setSelectedNinja((current) =>
          current && current.id === ninja.id
            ? ({ ...current, ...privateDetails } as NinjaProfile)
            : current
        );
      }
    } catch (error) {
      // Must be visible: a silent failure leaves the identity and banking
      // fields blank, which reads as "the partner submitted nothing" and could
      // get a valid application rejected.
      console.error('Could not load private ninja details:', error);
      setSnackbar({
        open: true,
        message: 'Could not load this partner\'s identity and banking details. Do not reject the application on this basis — retry first.',
        severity: 'error',
      });
    }
  };

  const getTabCounts = () => {
    return {
      // Issue #262: All tab excludes deleted ninjas
      all: ninjas.filter(n => n.verificationStatus !== 'deleted').length,
      pending: ninjas.filter(n => n.verificationStatus === 'pending').length,
      approved: ninjas.filter(n => n.verificationStatus === 'approved').length,
      rejected: ninjas.filter(n => n.verificationStatus === 'rejected').length,
      // Issue #262: Add deleted count for new Deleted tab
      deleted: ninjas.filter(n => n.verificationStatus === 'deleted').length,
    };
  };

  const tabCounts = getTabCounts();

  return (
    <Box>
      <PageHeader
        title="Ninjas"
        subtitle="Delivery partners and their verification status"
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={openAddNinja}>
            Add Ninja
          </Button>
        }
      />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Ninjas" value={tabCounts.all} icon={<Person />} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Pending Approval" value={tabCounts.pending} icon={<Edit />} tone="warning" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Approved" value={tabCounts.approved} icon={<VerifiedUser />} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Rejected" value={tabCounts.rejected} icon={<Block />} tone="error" />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search ninjas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Segment</InputLabel>
              <Select
                value={filterSegment}
                label="Segment"
                onChange={(e) => setFilterSegment(e.target.value)}
              >
                <MenuItem value="all">All Segments</MenuItem>
                {/* Use only segments from Services & Pricing configuration (service_segments collection) */}
                {Object.entries(serviceSegmentsMap).map(([segmentId, segmentName]) => (
                  <MenuItem key={segmentId} value={segmentId}>
                    {segmentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button startIcon={<Download />} variant="outlined" onClick={handleExportNinjas}>
              Export
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab 
              label={
                <Badge badgeContent={tabCounts.all} color="primary" showZero>
                  All Ninjas
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={tabCounts.pending} color="warning" showZero>
                  Pending
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={tabCounts.approved} color="success" showZero>
                  Approved
                </Badge>
              } 
            />
            <Tab
              label={
                <Badge badgeContent={tabCounts.rejected} color="error" showZero>
                  Rejected
                </Badge>
              }
            />
            {/* Issue #262: Add Deleted tab */}
            {/* Issue #276: Changed badge color to secondary for better visibility */}
            <Tab
              label={
                <Badge badgeContent={tabCounts.deleted} color="secondary" showZero>
                  Deleted
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <NinjaTable 
            ninjas={filteredNinjas} 
            onViewDetails={handleViewDetails}
            onApprove={handleApproveNinja}
            onReject={handleRejectNinja}
            loading={loading}
            serviceSegmentsMap={serviceSegmentsMap}
            serviceCategoriesMap={serviceCategoriesMap}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <NinjaTable 
            ninjas={filteredNinjas} 
            onViewDetails={handleViewDetails}
            onApprove={handleApproveNinja}
            onReject={handleRejectNinja}
            loading={loading}
            showActions={true}
            serviceSegmentsMap={serviceSegmentsMap}
            serviceCategoriesMap={serviceCategoriesMap}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <NinjaTable 
            ninjas={filteredNinjas} 
            onViewDetails={handleViewDetails}
            onApprove={handleApproveNinja}
            onReject={handleRejectNinja}
            loading={loading}
            serviceSegmentsMap={serviceSegmentsMap}
            serviceCategoriesMap={serviceCategoriesMap}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <NinjaTable
            ninjas={filteredNinjas}
            onViewDetails={handleViewDetails}
            onApprove={handleApproveNinja}
            onReject={handleRejectNinja}
            loading={loading}
            serviceSegmentsMap={serviceSegmentsMap}
            serviceCategoriesMap={serviceCategoriesMap}
          />
        </TabPanel>
        {/* Issue #262: Add Deleted tab panel */}
        <TabPanel value={tabValue} index={4}>
          <NinjaTable
            ninjas={filteredNinjas}
            onViewDetails={handleViewDetails}
            onApprove={handleApproveNinja}
            onReject={handleRejectNinja}
            loading={loading}
            serviceSegmentsMap={serviceSegmentsMap}
            serviceCategoriesMap={serviceCategoriesMap}
          />
        </TabPanel>
      </Card>

      {/* Enhanced Ninja Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="xl" 
        fullWidth
        fullScreen={false}
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            overflow: 'hidden',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2,
            px: 4,
            position: 'relative'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 2, sm: 4 }, 
            position: 'relative', 
            zIndex: 1,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
              {/* Issue #274: Simplified Avatar to match Task Details modal theme */}
              <Avatar
                src={(selectedNinja as any)?.profileImage || (selectedNinja as any)?.profileImageUrl || ''}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: { xs: 50, sm: 60 },
                  height: { xs: 50, sm: 60 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.3)'
                }}
              >
                {selectedNinja?.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <Box flex={1}>
                {/* Issue #274: Simplified typography to match Task Details modal */}
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                  {selectedNinja?.fullName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {selectedNinja?.email}
                </Typography>
                {/* Issue #274: Simplified chips to match Task Details modal */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    ID: {selectedNinja?.id.substring(0, 8)}...
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedNinja?.verificationStatus?.toUpperCase()}
                    sx={{
                      bgcolor: selectedNinja?.verificationStatus === 'approved'
                        ? 'rgba(76, 175, 80, 0.2)'
                        : selectedNinja?.verificationStatus === 'pending'
                        ? 'rgba(255, 152, 0, 0.2)'
                        : 'rgba(244, 67, 54, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                  <Chip
                    size="small"
                    icon={<Phone sx={{ fontSize: '0.8rem !important' }} />}
                    label={selectedNinja?.phoneNumber}
                    variant="outlined"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Quick Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              alignItems: 'center',
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' }
            }}>
              {/* Issue #274: Simplified close button to match Task Details modal */}
              <IconButton
                onClick={() => setDetailsOpen(false)}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedNinja && (
            <>
              {/* Issue #285: Only show Performance Overview if ninja has completed at least one job */}
              {(() => {
                const hasCompletedJobs = ninjaTasks && ninjaTasks.filter(t => t.status === 'completed').length > 0;
                return hasCompletedJobs ? (
                  <Box sx={{
                    p: 3,
                    bgcolor: 'action.hover',
                    position: 'relative'
                  }}>
                    <Box sx={{ maxWidth: '1200px', mx: 'auto', position: 'relative', zIndex: 1 }}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 4, color: 'text.primary', textAlign: 'center' }}>
                        Performance Overview
                      </Typography>
                  <Grid container spacing={3} sx={{ justifyContent: 'center', px: 2  }}>
                  <Grid item xs={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Avatar sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          mx: 'auto', 
                          mb: 2,
                          width: 56,
                          height: 56,
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                          <Assignment sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                          {selectedNinja.totalJobs ?? ninjaTasks.filter(t => t.status === 'completed').length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Jobs Completed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      bgcolor: 'success.main',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Avatar sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          mx: 'auto', 
                          mb: 2,
                          width: 56,
                          height: 56,
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                          <CurrencyRupee sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                          ₹{selectedNinja.totalEarnings ?? ninjaWallet?.totalCredits ?? 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Earnings
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      bgcolor: 'info.main',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Avatar sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          mx: 'auto', 
                          mb: 2,
                          width: 56,
                          height: 56,
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                          <AccountBalanceWallet sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                          ₹{ninjaWallet?.balance || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Wallet Balance
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{
                      height: '100%',
                      bgcolor: 'warning.main',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Avatar sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          mx: 'auto', 
                          mb: 2,
                          width: 56,
                          height: 56,
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                          <BadgeIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                          {selectedNinja.rating || 0}/5
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Average Rating
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                </Box>
              </Box>
                ) : null;
              })()}

              {/* Issue #239: Show deletion information for deleted accounts */}
              {selectedNinja.verificationStatus === 'deleted' && (
                <Box sx={{ p: 4, bgcolor: 'error.light' }}>
                  <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                    <Card sx={{
                      p: 3,
                      backgroundColor: (t) => alpha(t.palette.error.main, 0.1),
                      borderRadius: 2,
                      borderLeft: '6px solid',
                      borderColor: 'error.main',
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ fontSize: '1.5rem' }}>⚠️</Box>
                        Account Deleted
                      </Typography>
                      {selectedNinja.deletedAt && (
                        <Typography variant="body1" sx={{ mb: 1.5, color: 'text.primary' }}>
                          <strong>Deleted On:</strong> {new Date(selectedNinja.deletedAt).toLocaleString()}
                        </Typography>
                      )}
                      {selectedNinja.deletionReason && (
                        <Typography variant="body1" sx={{ mb: 1.5, color: 'text.primary' }}>
                          <strong>Reason:</strong> {selectedNinja.deletionReason}
                        </Typography>
                      )}
                      {selectedNinja.deletionDetails && (
                        <Typography variant="body1" sx={{ color: 'text.primary' }}>
                          <strong>Additional Details:</strong> {selectedNinja.deletionDetails}
                        </Typography>
                      )}
                    </Card>
                  </Box>
                </Box>
              )}

              {/* Enhanced Navigation Tabs */}
              <Box sx={(t) => ({
                borderBottom: 1, 
                borderColor: 'divider',
                px: 3,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                // A hardcoded `background:` shorthand gradient used to sit
                // here — near-white regardless of palette mode, and it wins
                // over any bgcolor in the same sx object because the
                // shorthand resets background-color. It silently overrode an
                // earlier fix that only touched bgcolor: this tab bar has
                // been a bright white strip in the middle of an otherwise-
                // dark dialog since before that fix, not a rendering
                // artifact of anything since.
                bgcolor: alpha(t.palette.background.paper, 0.85),
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: t.palette.mode === 'dark'
                  ? 'none'
                  : '0 2px 12px rgba(0,0,0,0.08)',
              })}>
                {/* Issue #273: Conditional tab locking logic based on data availability */}
                {(() => {
                  const hasWalletData = ninjaWallet && (ninjaWallet.balance > 0 || ninjaWallet.totalCredits > 0 || ninjaWallet.totalDebits > 0);
                  const hasTaskHistory = ninjaTasks && ninjaTasks.filter(t => t.status === 'completed').length > 0;
                  const hasTransactions = ninjaTransactions && ninjaTransactions.length > 0;

                  return (
                    <>
                      <Tabs
                        value={detailsTabValue}
                        onChange={(e, newValue) => {
                          // Issue #273: Prevent navigation to disabled tabs
                          if (newValue === 1 && !hasWalletData) return;
                          if (newValue === 2 && !hasTaskHistory) return;
                          if (newValue === 3 && !hasTransactions) return;
                          setDetailsTabValue(newValue);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                    '& .MuiTab-root': {
                      minHeight: 72,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: 'text.secondary',
                      transition: 'all 0.3s ease',
                      borderRadius: '12px 12px 0 0',
                      margin: '0 2px',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'transparent',
                        transition: 'background 0.3s ease'
                      },
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: (t) => `${t.palette.primary.main}0F`,
                        transform: 'translateY(-1px)',
                        '&::before': {
                          background: (t) => `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                        }
                      },
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 700,
                        backgroundColor: (t) => `${t.palette.primary.main}14`,
                        '&::before': {
                          background: (t) => `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                        }
                      }
                    },
                    '& .MuiTabs-indicator': {
                      height: 4,
                      borderRadius: '2px 2px 0 0',
                      background: (t) => `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                      boxShadow: (t) => `0 2px 8px ${t.palette.primary.main}4D`
                    },
                    '& .MuiTabs-scrollButtons': {
                      color: 'primary.main',
                      '&.Mui-disabled': {
                        opacity: 0.3
                      }
                    }
                  }}
                >
                  <Tab 
                    icon={<Person sx={{ fontSize: '1.1rem' }} />} 
                    iconPosition="start"
                    label="Personal Info" 
                    sx={{ 
                      gap: 1,
                      '& .MuiSvgIcon-root': { 
                        transition: 'transform 0.3s ease' 
                      },
                      '&:hover .MuiSvgIcon-root': { 
                        transform: 'scale(1.1)' 
                      }
                    }}
                  />
                        {/* Issue #273: Wallet tab - disabled if no wallet data */}
                        <Tab
                          icon={
                            !hasWalletData ? (
                              <Lock sx={{ fontSize: '1.1rem', opacity: 0.4 }} />
                            ) : (
                              <AccountBalanceWallet sx={{ fontSize: '1.1rem' }} />
                            )
                          }
                          iconPosition="start"
                          label={!hasWalletData ? "Wallet (No Data)" : "Wallet"}
                          disabled={!hasWalletData}
                          sx={{
                            gap: 1,
                            opacity: !hasWalletData ? 0.4 : 1,
                            cursor: !hasWalletData ? 'not-allowed' : 'pointer',
                            '& .MuiSvgIcon-root': {
                              transition: 'transform 0.3s ease'
                            },
                            '&:hover .MuiSvgIcon-root': {
                              transform: hasWalletData ? 'scale(1.1)' : 'none'
                            },
                            '&.Mui-disabled': {
                              color: 'text.disabled',
                              opacity: 0.4
                            }
                          }}
                        />
                        {/* Issue #273: Task History tab - disabled if no completed tasks */}
                        <Tab
                          icon={
                            !hasTaskHistory ? (
                              <Lock sx={{ fontSize: '1.1rem', opacity: 0.4 }} />
                            ) : (
                              <Assignment sx={{ fontSize: '1.1rem' }} />
                            )
                          }
                          iconPosition="start"
                          label={!hasTaskHistory ? "Task History (No Data)" : "Task History"}
                          disabled={!hasTaskHistory}
                          sx={{
                            gap: 1,
                            opacity: !hasTaskHistory ? 0.4 : 1,
                            cursor: !hasTaskHistory ? 'not-allowed' : 'pointer',
                            '& .MuiSvgIcon-root': {
                              transition: 'transform 0.3s ease'
                            },
                            '&:hover .MuiSvgIcon-root': {
                              transform: hasTaskHistory ? 'scale(1.1)' : 'none'
                            },
                            '&.Mui-disabled': {
                              color: 'text.disabled',
                              opacity: 0.4
                            }
                          }}
                        />
                        {/* Issue #273: Transactions tab - disabled if no transactions */}
                        <Tab
                          icon={
                            !hasTransactions ? (
                              <Lock sx={{ fontSize: '1.1rem', opacity: 0.4 }} />
                            ) : (
                              <Receipt sx={{ fontSize: '1.1rem' }} />
                            )
                          }
                          iconPosition="start"
                          label={!hasTransactions ? "Transactions (No Data)" : "Transactions"}
                          disabled={!hasTransactions}
                          sx={{
                            gap: 1,
                            opacity: !hasTransactions ? 0.4 : 1,
                            cursor: !hasTransactions ? 'not-allowed' : 'pointer',
                            '& .MuiSvgIcon-root': {
                              transition: 'transform 0.3s ease'
                            },
                            '&:hover .MuiSvgIcon-root': {
                              transform: hasTransactions ? 'scale(1.1)' : 'none'
                            },
                            '&.Mui-disabled': {
                              color: 'text.disabled',
                              opacity: 0.4
                            }
                          }}
                        />
                        {/* Issue #273: Documents tab - always accessible */}
                        <Tab
                          icon={<BadgeIcon sx={{ fontSize: '1.1rem' }} />}
                          iconPosition="start"
                          label="Documents"
                          sx={{
                            gap: 1,
                            '& .MuiSvgIcon-root': {
                              transition: 'transform 0.3s ease'
                            },
                            '&:hover .MuiSvgIcon-root': {
                              transform: 'scale(1.1)'
                            }
                          }}
                        />
                      </Tabs>
                    </>
                  );
                })()}
              </Box>

              {/* Personal Information Tab */}
              <TabPanel value={detailsTabValue} index={0}>
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          height: '100%',
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                          border: '1px solid rgba(0,0,0,0.06)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                            transform: 'translateY(-2px)'
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2, 
                              mb: 3,
                              pb: 2,
                              borderBottom: (t) => `2px solid ${t.palette.divider}`
                            }}>
                              <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: 40, 
                                height: 40,
                                boxShadow: (t) => `0 4px 12px ${t.palette.primary.main}4D`
                              }}>
                                <Person />
                              </Avatar>
                              <Typography variant="h6" fontWeight={700} color="text.primary">
                                Personal Details
                              </Typography>
                            </Box>
                            <List sx={{ py: 0 }}>
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText 
                                  primary={<Typography variant="subtitle2" color="text.secondary">Full Name</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{selectedNinja.fullName || selectedNinja.email || 'No Name'}</Typography>}
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText
                                  primary={<Typography variant="subtitle2" color="text.secondary">Phone</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{selectedNinja.phoneNumber || selectedNinja.phone || 'No Phone'}</Typography>}
                                />
                              </ListItem>
                              {/* Only show email if it exists - email is not required for ninja registration */}
                              {selectedNinja.email && (
                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                  <ListItemText
                                    primary={<Typography variant="subtitle2" color="text.secondary">Email</Typography>}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{selectedNinja.email}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {/* Issue #186: Add Date of Birth */}
                              {selectedNinja.dateOfBirth && (
                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                  <ListItemText
                                    primary={<Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{selectedNinja.dateOfBirth}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {/* Issue #186: Add Gender */}
                              {selectedNinja.gender && (
                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                  <ListItemText
                                    primary={<Typography variant="subtitle2" color="text.secondary">Gender</Typography>}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={<Typography variant="body1" fontWeight={500} color="text.primary" sx={{ textTransform: 'capitalize' }}>{selectedNinja.gender}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {/* Issue #217: Address fields moved to Address & Service Area section */}
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText 
                                  primary={<Typography variant="subtitle2" color="text.secondary">Joined On</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{new Date(selectedNinja.createdAt).toLocaleDateString()}</Typography>}
                                />
                              </ListItem>
                              {selectedNinja.approvedAt && (
                                <ListItem sx={{ px: 0, py: 1.5 }}>
                                  <ListItemText 
                                    primary={<Typography variant="subtitle2" color="text.secondary">Approved On</Typography>}
                                    secondaryTypographyProps={{ component: 'div' }}
                                    secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{new Date(selectedNinja.approvedAt).toLocaleDateString()}</Typography>}
                                  />
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          height: '100%',
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                          border: '1px solid rgba(0,0,0,0.06)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                            transform: 'translateY(-2px)'
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2, 
                              mb: 2,
                              pb: 2,
                              borderBottom: (t) => `2px solid ${t.palette.divider}`
                            }}>
                              <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: 40, 
                                height: 40,
                                boxShadow: (t) => `0 4px 12px ${t.palette.primary.main}4D`
                              }}>
                                <LocationOn />
                              </Avatar>
                              <Typography variant="h6" fontWeight={700} color="text.primary">
                                Address & Service Area
                              </Typography>
                            </Box>
                            <List sx={{ py: 0 }}>
                              {/* Issue #217: Display Permanent Address */}
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText
                                  primary={<Typography variant="subtitle2" color="text.secondary">Permanent Address</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={
                                    <Typography variant="body1" fontWeight={500} color="text.primary">
                                      {selectedNinja.permanentAddress
                                        ? (typeof selectedNinja.permanentAddress === 'string'
                                            ? selectedNinja.permanentAddress
                                            : `${selectedNinja.permanentAddress.street || ''}, ${selectedNinja.permanentAddress.city || ''}, ${selectedNinja.permanentAddress.state || ''} - ${selectedNinja.permanentAddress.pincode || ''}`)
                                        : 'Not provided'}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              {/* Issue #217: Display Current Address */}
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText
                                  primary={<Typography variant="subtitle2" color="text.secondary">Current Address</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={
                                    <Typography variant="body1" fontWeight={500} color="text.primary">
                                      {selectedNinja.currentAddress
                                        ? (typeof selectedNinja.currentAddress === 'string'
                                            ? selectedNinja.currentAddress
                                            : `${selectedNinja.currentAddress.street || ''}, ${selectedNinja.currentAddress.city || ''}, ${selectedNinja.currentAddress.state || ''} - ${selectedNinja.currentAddress.pincode || ''}`)
                                        : 'Not provided'}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              {/* Issue #217: Display Address (legacy format) */}
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText
                                  primary={<Typography variant="subtitle2" color="text.secondary">Service Segments</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                      {selectedNinja.selectedSegments?.map((segment) => {
                                        const segmentName = serviceSegmentsMap[segment] || serviceCategoriesMap[segment] || serviceSegmentLabels[segment] || segment;
                                        return (
                                          <Chip
                                            key={segment}
                                            label={segmentName}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontWeight: 500 }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  }
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText 
                                  primary={<Typography variant="subtitle2" color="text.secondary">Service Radius</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={<Typography variant="body1" fontWeight={500} color="text.primary">{`${selectedNinja.serviceArea?.radius || 0} km`}</Typography>} 
                                />
                              </ListItem>
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemText
                                  primary={<Typography variant="subtitle2" color="text.secondary">Delivery Capabilities</Typography>}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  secondary={
                                    selectedNinja.deliveryCapabilities ? (
                                      <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Grid container spacing={3}>
                                          <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px', fontWeight: 500 }}>
                                                  Vehicle:
                                                </Typography>
                                                <Chip 
                                                  label={selectedNinja.deliveryCapabilities.vehicleType || 'Not specified'} 
                                                  size="small" 
                                                  color="primary"
                                                  variant="filled"
                                                  sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                                                />
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px', fontWeight: 500 }}>
                                                  Max Weight:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                                  {selectedNinja.deliveryCapabilities.maxWeight || 0} kg
                                                </Typography>
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px', fontWeight: 500 }}>
                                                  Max Distance:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                                  {selectedNinja.deliveryCapabilities.maxDistance || 0} km
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '90px', fontWeight: 500 }}>
                                                  Fragile Items:
                                                </Typography>
                                                <Chip 
                                                  label={selectedNinja.deliveryCapabilities.fragileItems ? 'Accepted' : 'Not Accepted'} 
                                                  size="small" 
                                                  color={selectedNinja.deliveryCapabilities.fragileItems ? 'success' : 'default'}
                                                  variant="outlined"
                                                  sx={{ fontWeight: 500 }}
                                                />
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '90px', fontWeight: 500 }}>
                                                  Refrigerated:
                                                </Typography>
                                                <Chip 
                                                  label={selectedNinja.deliveryCapabilities.refrigerated ? 'Available' : 'Not Available'} 
                                                  size="small" 
                                                  color={selectedNinja.deliveryCapabilities.refrigerated ? 'success' : 'default'}
                                                  variant="outlined"
                                                  sx={{ fontWeight: 500 }}
                                                />
                                              </Box>
                                            </Box>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        No delivery capabilities specified
                                      </Typography>
                                    )
                                  } 
                                />
                              </ListItem>
                            </List>
                        </CardContent>
                      </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccountBalance /> Banking Details
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <ListItem>
                                  <ListItemText 
                                    primary="Account Number" 
                                    secondary={selectedNinja.bankingDetails?.accountNumber || selectedNinja.bankAccountNumber || 'Not provided'} 
                                  />
                                </ListItem>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <ListItem>
                                  <ListItemText 
                                    primary="IFSC Code" 
                                    secondary={selectedNinja.bankingDetails?.ifscCode || selectedNinja.ifscCode || 'Not provided'} 
                                  />
                                </ListItem>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <ListItem>
                                  <ListItemText 
                                    primary="Bank Name" 
                                    secondary={selectedNinja.bankingDetails?.bankName || 'Not provided'} 
                                  />
                                </ListItem>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </TabPanel>

              {/* Wallet Tab */}
              <TabPanel value={detailsTabValue} index={1}>
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountBalanceWallet /> Wallet Overview
                        </Typography>
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="h3" fontWeight={600} color="primary.main">
                            ₹{ninjaWallet?.availableBalance || ninjaWallet?.balance || 0}
                          </Typography>
                          <Typography variant="subtitle1" color="text.secondary">
                            Current Balance
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <List>
                          <ListItem>
                            <ListItemText
                              primary="Total Credits"
                              secondary={`₹${ninjaWallet?.totalEarnings || ninjaWallet?.totalCredits || 0}`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Total Debits"
                              secondary={`₹${ninjaWallet?.totalWithdrawn || ninjaWallet?.totalDebits || 0}`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Last Updated"
                              secondary={ninjaWallet?.updatedAt ? (ninjaWallet.updatedAt.toDate ? new Date(ninjaWallet.updatedAt.toDate()).toLocaleDateString() : new Date(ninjaWallet.updatedAt).toLocaleDateString()) : 'Never'}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recent Wallet Activity
                        </Typography>
                        {ninjaTransactions.length > 0 ? (
                          <List>
                            {ninjaTransactions.slice(0, 5).map((transaction, index) => (
                              <ListItem key={index}>
                                <ListItemText
                                  primary={`${transaction.type === 'credit' ? '+' : '-'}₹${transaction.amount}`}
                                  secondary={`${transaction.description} - ${new Date(transaction.createdAt.toDate()).toLocaleDateString()}`}
                                />
                                <Chip
                                  size="small"
                                  label={transaction.type}
                                  color={transaction.type === 'credit' ? 'success' : 'error'}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            No wallet activity found
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                </Box>
              </TabPanel>

              {/* Task History Tab */}
              <TabPanel value={detailsTabValue} index={2}>
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment /> Task History
                    </Typography>
                    {ninjaTasks.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Task ID</TableCell>
                              <TableCell>Title</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Rating</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ninjaTasks.map((task) => (
                              <TableRow key={task.id}>
                                <TableCell>{task.orderId}</TableCell>
                                <TableCell>{task.title}</TableCell>
                                <TableCell>
                                  <StatusChip status={task.status} />
                                </TableCell>
                                <TableCell>₹{task.finalPrice || task.estimatedPrice}</TableCell>
                                <TableCell>{new Date(task.createdAt.toDate()).toLocaleDateString()}</TableCell>
                                <TableCell>{task.rating || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No tasks found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
                </Box>
              </TabPanel>

              {/* Transactions Tab */}
              <TabPanel value={detailsTabValue} index={3}>
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Receipt /> Transaction History
                    </Typography>
                    {ninjaTransactions.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Balance After</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(() => {
                              // Calculate running balance from oldest to newest, then display in reverse order
                              const sortedTransactions = [...ninjaTransactions].sort((a, b) => {
                                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                                return dateA.getTime() - dateB.getTime();
                              });

                              let runningBalance = 0;
                              const transactionsWithBalance = sortedTransactions.map(transaction => {
                                if (transaction.type === 'credit') {
                                  runningBalance += transaction.amount;
                                } else {
                                  runningBalance -= transaction.amount;
                                }
                                return { ...transaction, calculatedBalance: runningBalance };
                              });

                              // Reverse to show newest first
                              return transactionsWithBalance.reverse().map((transaction, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {new Date(transaction.createdAt?.toDate?.() || transaction.createdAt).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={transaction.type}
                                      color={transaction.type === 'credit' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography color={transaction.type === 'credit' ? 'success.main' : 'error.main'}>
                                      {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{transaction.description}</TableCell>
                                  <TableCell>₹{transaction.balanceAfter ?? transaction.calculatedBalance ?? 0}</TableCell>
                                </TableRow>
                              ));
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No transactions found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
                </Box>
              </TabPanel>

              {/* Documents Tab - Issue #186: Enhanced to show all document details */}
              <TabPanel value={detailsTabValue} index={4}>
                <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <Grid container spacing={3}>
                    {/* PAN Card */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon sx={{ color: 'primary.main' }} />
                            PAN Card
                          </Typography>
                          {selectedNinja.panCardUrl || selectedNinja.panCardImage ? (
                            <Box>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={async () => {
                                  const url = await getDocumentUrl(selectedNinja.panCardUrl || selectedNinja.panCardImage || '');
                                  setDocumentPreviewFailed(false);
                                  setDocumentModal({ open: true, type: 'PAN Card', url });
                                }}
                                sx={{
                                  mb: 2,
                                  background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                                  '&:hover': {
                                    background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.dark} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: (t) => `0 8px 24px ${t.palette.primary.main}4D`
                                  }
                                }}
                              >
                                View PAN Card Image
                              </Button>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                PAN Number: {selectedNinja.panCard || 'Not provided'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">No PAN card uploaded</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Aadhaar Card Front */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon sx={{ color: 'info.main' }} />
                            Aadhaar Card (Front)
                          </Typography>
                          {selectedNinja.aadharCardUrl || selectedNinja.aadharCardImage ? (
                            <Box>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={async () => {
                                  const url = await getDocumentUrl(selectedNinja.aadharCardUrl || selectedNinja.aadharCardImage || '');
                                  setDocumentPreviewFailed(false);
                                  setDocumentModal({ open: true, type: 'Aadhaar Card (Front)', url });
                                }}
                                sx={{
                                  mb: 2,
                                  background: (t) => `linear-gradient(135deg, ${t.palette.info.main} 0%, ${t.palette.info.light} 100%)`,
                                  '&:hover': {
                                    background: (t) => `linear-gradient(135deg, ${t.palette.info.dark} 0%, ${t.palette.info.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: (t) => `0 8px 24px ${t.palette.info.main}4D`
                                  }
                                }}
                              >
                                View Aadhaar Front Image
                              </Button>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                Aadhaar Number: {selectedNinja.aadharCard || 'Not provided'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">No Aadhaar card front uploaded</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Issue #186: Aadhaar Card Back */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon sx={{ color: 'info.main' }} />
                            Aadhaar Card (Back)
                          </Typography>
                          {selectedNinja.aadharBackUrl || selectedNinja.aadharBackImage ? (
                            <Box>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={async () => {
                                  const url = await getDocumentUrl(selectedNinja.aadharBackUrl || selectedNinja.aadharBackImage || '');
                                  setDocumentPreviewFailed(false);
                                  setDocumentModal({ open: true, type: 'Aadhaar Card (Back)', url });
                                }}
                                sx={{
                                  mb: 2,
                                  background: (t) => `linear-gradient(135deg, ${t.palette.info.main} 0%, ${t.palette.info.light} 100%)`,
                                  '&:hover': {
                                    background: (t) => `linear-gradient(135deg, ${t.palette.info.dark} 0%, ${t.palette.info.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: (t) => `0 8px 24px ${t.palette.info.main}4D`
                                  }
                                }}
                              >
                                View Aadhaar Back Image
                              </Button>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">No Aadhaar card back uploaded</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Issue #186: Driving License */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon sx={{ color: 'success.main' }} />
                            Driving License
                          </Typography>
                          {selectedNinja.dlUrl || selectedNinja.dlImage ? (
                            <Box>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={async () => {
                                  const url = await getDocumentUrl(selectedNinja.dlUrl || selectedNinja.dlImage || '');
                                  setDocumentPreviewFailed(false);
                                  setDocumentModal({ open: true, type: 'Driving License', url });
                                }}
                                sx={{
                                  mb: 2,
                                  background: (t) => `linear-gradient(135deg, ${t.palette.success.dark} 0%, ${t.palette.success.light} 100%)`,
                                  '&:hover': {
                                    background: (t) => `linear-gradient(135deg, ${t.palette.success.dark} 0%, ${t.palette.success.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: (t) => `0 8px 24px ${t.palette.success.main}4D`
                                  }
                                }}
                              >
                                View Driving License Image
                              </Button>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                DL Number: {selectedNinja.dlNumber || 'Not provided'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography color="text.secondary">No driving license uploaded</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          bgcolor: 'background.default',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          gap: 3,
          justifyContent: 'space-between',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: (t) => `linear-gradient(90deg, transparent 0%, ${t.palette.primary.main}4D 50%, transparent 100%)`
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 0.5
          }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Last updated: {selectedNinja?.updatedAt ? new Date(selectedNinja.updatedAt).toLocaleDateString() : 'Never'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Profile ID: {selectedNinja?.id.substring(0, 12)}...
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Show Reject button for pending ninjas - Opens field-level reject modal */}
            {selectedNinja?.verificationStatus === 'pending' && (
              <Button
                onClick={handleOpenRejectModal}
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  background: (t) => `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`,
                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: (t) => `linear-gradient(135deg, ${t.palette.error.dark} 0%, ${t.palette.error.dark} 100%)`,
                    boxShadow: '0 8px 24px rgba(244, 67, 54, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Reject Ninja
              </Button>
            )}
            {/* Show Approve button for pending OR rejected ninjas */}
            {(selectedNinja?.verificationStatus === 'pending' || selectedNinja?.verificationStatus === 'rejected') && (
              <Button
                onClick={() => selectedNinja && handleApproveNinja(selectedNinja.id)}
                variant="contained"
                color="success"
                disabled={approvingNinja} // Issue #306: Disable while loading
                startIcon={approvingNinja ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />} // Issue #306: Show loader
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  background: (t) => `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.dark} 100%)`,
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: (t) => `linear-gradient(135deg, ${t.palette.success.dark} 0%, ${t.palette.success.dark} 100%)`,
                    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {approvingNinja ? 'Approving...' : (selectedNinja?.verificationStatus === 'rejected' ? 'Re-Approve Ninja' : 'Approve Ninja')}
              </Button>
            )}
            <Button 
              onClick={() => setDetailsOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                borderWidth: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (t) => `${t.palette.primary.main}0A`,
                  borderWidth: 2,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Document Modal */}
      <Dialog 
        open={documentModal.open} 
        onClose={() => setDocumentModal({ open: false, type: '', url: '' })}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {documentModal.type}
          </Typography>
          <IconButton 
            onClick={() => setDocumentModal({ open: false, type: '', url: '' })}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {documentModal.url ? (
            <Box sx={{ 
              width: '100%', 
              height: '70vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover'
            }}>
              {/* Check if it's an image file - handle both regular URLs and Firebase Storage URLs */}
              {(() => {
                const url = documentModal.url.toLowerCase();
                // Check for common image extensions or Firebase Storage image URLs
                const isImage = url.includes('.jpg') ||
                  url.includes('.jpeg') ||
                  url.includes('.png') ||
                  url.includes('.gif') ||
                  url.includes('.webp') ||
                  url.includes('firebasestorage.googleapis.com') ||
                  url.includes('storage.googleapis.com');

                // Skip file:// URLs as they won't work in browser
                const isValidUrl = !url.startsWith('file://') && (url.startsWith('http://') || url.startsWith('https://'));

                if (!isValidUrl) {
                  return (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                      <Typography variant="h6" color="error" gutterBottom>
                        Unable to Display Document
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The document URL is not accessible in the browser.
                        The document may have been uploaded with a local file path.
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 2, wordBreak: 'break-all' }}>
                        URL: {documentModal.url}
                      </Typography>
                    </Box>
                  );
                }

                if (isImage) {
                  // The fallback used to be assigned through `parent.innerHTML`
                  // with the document URL concatenated into an href. Those URLs
                  // come from ninja uploads, so a quote in one closed the
                  // attribute and injected arbitrary markup into the admin
                  // panel. Rendering it as React escapes the value for free.
                  if (documentPreviewFailed) {
                    return (
                      <Box sx={{ textAlign: 'center', p: 5 }}>
                        <Typography color="text.secondary" gutterBottom>
                          Failed to load image.
                        </Typography>
                        <Button
                          component="a"
                          href={documentModal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open in a new tab
                        </Button>
                      </Box>
                    );
                  }

                  return (
                    <img
                      src={documentModal.url}
                      alt={documentModal.type}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '0 0 12px 12px'
                      }}
                      onError={() => {
                        console.error('Image failed to load:', documentModal.url);
                        setDocumentPreviewFailed(true);
                      }}
                    />
                  );
                }

                // For PDF and other document types
                return (
                  <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                    <iframe
                      src={`${documentModal.url}#view=FitH`}
                      title={documentModal.type}
                      width="100%"
                      height="100%"
                      style={{
                        border: 'none',
                        borderRadius: '0 0 12px 12px'
                      }}
                      onError={() => {
                        console.error('Iframe failed to load:', documentModal.url);
                      }}
                    />
                    {/* Fallback link if iframe fails */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      bgcolor: 'rgba(0,0,0,0.7)',
                      borderRadius: 2,
                      p: 1
                    }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => window.open(documentModal.url, '_blank')}
                        sx={{ color: 'white' }}
                      >
                        Open in New Tab
                      </Button>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center',
              height: '30vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No document available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The document URL is not available or may have expired.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          bgcolor: 'background.default',
          gap: 2
        }}>
          <Button 
            variant="outlined"
            onClick={() => window.open(documentModal.url, '_blank')}
            startIcon={<Visibility />}
            sx={{ borderRadius: 2 }}
          >
            Open in New Tab
          </Button>
          <Button 
            variant="contained"
            onClick={() => setDocumentModal({ open: false, type: '', url: '' })}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field-Level Reject Modal - Issue #165 */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
          }
        }}
      >
        <DialogTitle sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`,
          color: 'white',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 2,
        }}>
          <Cancel /> Reject Ninja - Select Fields to Reject
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', minHeight: '60vh', maxHeight: '70vh' }}>
            {/* LEFT: Field Selection */}
            <Box sx={{
              flex: 1,
              p: 3,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'auto', // Issue #299: Add independent scrollbar for left section
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Select Fields with Issues
              </Typography>

              {/* Personal Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                  Personal Details
                </Typography>
                <FormGroup>
                  {rejectableFields.personalDetails.map(field => (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={selectedFieldsForRejection.has(field.id)}
                          onChange={() => handleFieldToggle(field.id)}
                          sx={{ '&.Mui-checked': { color: 'error.main' } }}
                        />
                      }
                      label={field.label}
                      sx={{
                        bgcolor: selectedFieldsForRejection.has(field.id) ? 'error.lighter' : 'transparent',
                        borderRadius: 1,
                        px: 1,
                        transition: 'background-color 0.2s',
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Address Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                  Address Details
                </Typography>
                <FormGroup>
                  {rejectableFields.addressDetails.map(field => (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={selectedFieldsForRejection.has(field.id)}
                          onChange={() => handleFieldToggle(field.id)}
                          sx={{ '&.Mui-checked': { color: 'error.main' } }}
                        />
                      }
                      label={field.label}
                      sx={{
                        bgcolor: selectedFieldsForRejection.has(field.id) ? 'error.lighter' : 'transparent',
                        borderRadius: 1,
                        px: 1,
                        transition: 'background-color 0.2s',
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Document Numbers */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                  Document Numbers
                </Typography>
                <FormGroup>
                  {rejectableFields.documentNumbers.map(field => (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={selectedFieldsForRejection.has(field.id)}
                          onChange={() => handleFieldToggle(field.id)}
                          sx={{ '&.Mui-checked': { color: 'error.main' } }}
                        />
                      }
                      label={field.label}
                      sx={{
                        bgcolor: selectedFieldsForRejection.has(field.id) ? 'error.lighter' : 'transparent',
                        borderRadius: 1,
                        px: 1,
                        transition: 'background-color 0.2s',
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Document Images */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                  Document Images
                </Typography>
                <FormGroup>
                  {rejectableFields.documentImages.map(field => (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={selectedFieldsForRejection.has(field.id)}
                          onChange={() => handleFieldToggle(field.id)}
                          sx={{ '&.Mui-checked': { color: 'error.main' } }}
                        />
                      }
                      label={field.label}
                      sx={{
                        bgcolor: selectedFieldsForRejection.has(field.id) ? 'error.lighter' : 'transparent',
                        borderRadius: 1,
                        px: 1,
                        transition: 'background-color 0.2s',
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>

            {/* RIGHT: Reason Selection */}
            <Box sx={{
              flex: 1,
              p: 3,
              bgcolor: 'action.hover',
              overflow: 'auto', // Issue #299: Add independent scrollbar for right section
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Select Rejection Reason for Each Field
              </Typography>

              {selectedFieldsForRejection.size === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  <Typography variant="body1">
                    Select fields on the left to provide rejection reasons
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {Array.from(selectedFieldsForRejection).map(fieldId => {
                    // Find the field definition
                    const allFields = [
                      ...rejectableFields.personalDetails,
                      ...rejectableFields.addressDetails,
                      ...rejectableFields.documentNumbers,
                      ...rejectableFields.documentImages,
                    ];
                    const field = allFields.find(f => f.id === fieldId);
                    if (!field) return null;

                    const isImageField = field.type === 'image';
                    const reasons = isImageField ? imageFieldReasons : textFieldReasons;

                    return (
                      <Box
                        key={fieldId}
                        sx={{
                          p: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: rejectionReasons[fieldId] ? 'success.main' : 'warning.main',
                          borderLeftWidth: 4,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          {field.label}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Reason</InputLabel>
                          <Select
                            value={rejectionReasons[fieldId] || ''}
                            onChange={(e) => handleReasonChange(fieldId, e.target.value)}
                            label="Select Reason"
                            sx={{
                              bgcolor: 'background.paper',
                              '& .MuiSelect-select': {
                                fontWeight: rejectionReasons[fieldId] ? 500 : 400,
                              }
                            }}
                          >
                            {reasons.map(reason => (
                              <MenuItem key={reason} value={reason}>
                                {reason}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>

          {/* Validation Message */}
          {selectedFieldsForRejection.size > 0 && !isRejectSubmitValid() && (
            <Alert severity="warning" sx={{ mx: 3, mb: 2 }}>
              Please select a rejection reason for all selected fields before submitting.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          bgcolor: 'background.default',
          gap: 2,
          justifyContent: 'space-between',
        }}>
          <Typography variant="body2" color="text.secondary">
            {selectedFieldsForRejection.size} field(s) selected for rejection
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setRejectModalOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmitRejection}
              disabled={!isRejectSubmitValid() || rejectingNinja} // Issue #306: Disable while loading or invalid
              startIcon={rejectingNinja ? <CircularProgress size={20} color="inherit" /> : <Cancel />} // Issue #306: Show loader
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                background: isRejectSubmitValid() && !rejectingNinja
                  ? (t) => `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`
                  : undefined,
                '&:hover': {
                  background: (t) => `linear-gradient(135deg, ${t.palette.error.dark} 0%, ${t.palette.error.dark} 100%)`,
                },
              }}
            >
              {rejectingNinja ? 'Rejecting...' : 'Reject Ninja'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Ninja (requirement #1) */}
      <Dialog open={addNinjaOpen} onClose={() => setAddNinjaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Ninja</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Full Name"
              value={newNinjaName}
              onChange={(e) => setNewNinjaName(e.target.value)}
            />
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="+91XXXXXXXXXX"
              value={newNinjaPhone}
              onChange={(e) => setNewNinjaPhone(e.target.value)}
              helperText="E.164 format — this becomes how they sign in on the mobile app"
            />
            <TextField
              fullWidth
              label="Email (optional)"
              type="email"
              value={newNinjaEmail}
              onChange={(e) => setNewNinjaEmail(e.target.value)}
            />
            {!isActingAsCityAdmin ? (
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  label="City"
                  value={newNinjaCityId}
                  onChange={(e) => setNewNinjaCityId(e.target.value as string)}
                >
                  {addNinjaCities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}, {c.state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="City"
                value={effectiveCityId ? 'Your city' : 'No city assigned'}
                disabled
                helperText={!effectiveCityId ? 'Your admin account has no city — contact a super admin' : undefined}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNinjaOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddNinja}
            disabled={
              addNinjaSaving ||
              !newNinjaName.trim() ||
              !newNinjaPhone.trim() ||
              (isActingAsCityAdmin ? !effectiveCityId : !newNinjaCityId)
            }
          >
            {addNinjaSaving ? 'Adding…' : 'Add Ninja'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface NinjaTableProps {
  ninjas: NinjaProfile[];
  onViewDetails: (ninja: NinjaProfile) => void;
  onApprove: (ninjaId: string) => void;
  onReject: (ninjaId: string) => void;
  loading: boolean;
  showActions?: boolean;
  serviceSegmentsMap: { [key: string]: string };
  serviceCategoriesMap: { [key: string]: string };
}

const NinjaTable: React.FC<NinjaTableProps> = ({ 
  ninjas, 
  onViewDetails, 
  onApprove, 
  onReject, 
  loading, 
  showActions = false,
  serviceSegmentsMap,
  serviceCategoriesMap
}) => {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading ninjas...</Typography>
      </Box>
    );
  }

  if (ninjas.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No ninjas found</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveTable
      columns={[
        {
          key: 'ninja',
          header: 'Ninja',
          mobile: 'title',
          render: (ninja: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Issue #266: profile image when present, initial otherwise. */}
              <Avatar
                src={ninja.profileImage || ninja.profileImageUrl || ''}
                alt=""
                sx={{ mr: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}
              >
                {(ninja.fullName || ninja.email || 'N').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {ninja.fullName || ninja.email || 'No Name'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {ninja.id.substring(0, 8)}…
                </Typography>
              </Box>
            </Box>
          ),
        },
        {
          key: 'contact',
          header: 'Contact',
          render: (ninja: any) => (
            <>
              {/* Phone is the primary contact; email is rare/legacy here. */}
              <Typography variant="body2">
                {ninja.phoneNumber || ninja.phone || 'No Phone'}
              </Typography>
              {ninja.email && (
                <Typography variant="caption" color="text.secondary">
                  {ninja.email}
                </Typography>
              )}
            </>
          ),
        },
        {
          key: 'segments',
          header: 'Service Segments',
          render: (ninja: any) => (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
              }}
            >
              {(ninja.selectedSegments || []).map((segment: string) => (
                <Chip
                  key={segment}
                  label={
                    serviceSegmentsMap[segment] ||
                    serviceCategoriesMap[segment] ||
                    segment
                  }
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          ),
        },
        {
          key: 'status',
          header: 'Status',
          render: (ninja: any) => <StatusChip status={ninja.verificationStatus} />,
        },
        {
          key: 'joined',
          header: 'Joined',
          render: (ninja: any) => (
            <Typography variant="body2">
              {new Date(ninja.createdAt).toLocaleDateString()}
            </Typography>
          ),
        },
        {
          /* Issue #213: approve/reject live in the detail view, not the row. */
          key: 'actions',
          header: 'Actions',
          align: 'right',
          mobile: 'actions',
          render: (ninja: any) => (
            <IconButton
              onClick={() => onViewDetails(ninja)}
              size="small"
              aria-label="View ninja details"
            >
              <Visibility />
            </IconButton>
          ),
        },
      ]}
      rows={ninjas}
      rowKey={(ninja) => ninja.id}
      onRowClick={onViewDetails}
      aria-label="Ninjas"
      emptyTitle="No ninjas found"
      emptyDescription="Delivery partners appear here once they register. Try a different filter if you expected to see some."
    />
  );
};

export default NinjaManagement;